/**
 * Book-level audio export helpers (chapter masters → zip listing / m4b guidance).
 * Actual m4b muxing requires ffmpeg on PATH; we prepare chapter order + metadata.
 */

export type ChapterExportEntry = {
  order: number;
  chapterId: string;
  title: string;
  masterPath?: string;
  durationSec?: number;
};

export type AudiobookExportManifest = {
  title: string;
  author: string;
  narrator: string;
  chapters: ChapterExportEntry[];
  /** Suggested ffmpeg concat for m4b when ffmpeg is available. */
  ffmpegHint: string;
  whispersyncNotes: string[];
};

export function buildAudiobookExportManifest(params: {
  title: string;
  author: string;
  narrator: string;
  chapters: ChapterExportEntry[];
}): AudiobookExportManifest {
  const ordered = [...params.chapters].sort((a, b) => a.order - b.order);
  const listFile = ordered
    .filter((c) => c.masterPath)
    .map((c) => `file '${c.masterPath!.replace(/'/g, "'\\''")}'`)
    .join("\n");

  const ffmpegHint = listFile
    ? `# Write the lines below to chapters.txt then run:\n# ffmpeg -f concat -safe 0 -i chapters.txt -c copy book.m4b\n${listFile}`
    : "# Stitch each chapter first so master.wav paths exist.";

  return {
    title: params.title,
    author: params.author,
    narrator: params.narrator,
    chapters: ordered,
    ffmpegHint,
    whispersyncNotes: [
      "Amazon WhisperSync / Immersion Reading is proprietary — eligibility depends on ~97% text fidelity after ACX delivery.",
      "Keep narration unabridged and matched to chapter text (no ad-libs).",
      "Export ACX-compliant 192kbps CBR MP3 at 44.1kHz with RMS −23…−18 dB.",
      "In-app karaoke uses estimated or Whisper word alignment sidecars — not Amazon WhisperSync packages.",
    ],
  };
}

/**
 * Actionable ACX upload checklist lines for a chapter master.
 * Safe to show in Narrator Mode after stitch + validation.
 */
export function buildAcxChapterMasterChecklist(
  chapterTitle: string,
  hasMaster: boolean,
  acxPassed: boolean
): string[] {
  const title = chapterTitle.trim() || "Untitled chapter";
  const lines: string[] = [];
  if (!hasMaster) {
    lines.push(`Stitch "${title}" to produce a chapter master WAV before ACX upload.`);
    lines.push("Record or import takes covering every paragraph, then run Stitch.");
    return lines;
  }
  if (!acxPassed) {
    lines.push(`Master exists for "${title}" but ACX checks still need attention.`);
    lines.push("Fix failing RMS / peak / noise / spacing checks, then re-stitch.");
    lines.push("Confirm sample rate is 44.1 kHz mono (or stereo if required by your rights).");
    return lines;
  }
  lines.push(`Chapter master ready for ACX upload: "${title}".`);
  lines.push("Export or encode to ACX MP3 (192 kbps CBR, 44.1 kHz) if not already.");
  lines.push("Upload the chapter file in ACX in manuscript order; keep room tone / spacing intact.");
  lines.push("Verify opening/closing credits and retail sample rules separately if this is chapter 1.");
  return lines;
}

/** Soft update of book mean RMS after a chapter master is produced. */
export function updateBookMeanRms(
  previousMean: number | undefined,
  chapterRmsDb: number,
  chapterCountHint = 1
): number {
  if (typeof previousMean !== "number" || !Number.isFinite(previousMean)) {
    return chapterRmsDb;
  }
  const n = Math.max(1, chapterCountHint);
  // Exponential-ish blend toward new chapter
  const weight = 1 / Math.min(n, 12);
  return previousMean * (1 - weight) + chapterRmsDb * weight;
}
